package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.SceneKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.t

/** Saved documents may omit optional names. Presentation must never produce an unnamed row. */
internal fun layerDisplayName(layer: Layer): String = layer.base.name.trim().ifEmpty {
    when (layer) {
        is TextLayer -> (layer.content as? TextContent.Literal)?.text?.trim()?.takeIf { it.isNotEmpty() }
            ?: tr("studio.editor.editor_presentation.caption", "Caption")
        is DeviceLayer -> tr("studio.editor.editor_presentation.device_frame", "Device frame")
        is ImageLayer -> tr("studio.editor.editor_presentation.capture", "Capture")
        is BackgroundLayer -> tr("studio.editor.editor_presentation.background", "Background")
        is GroupLayer -> tr("studio.editor.editor_presentation.group", "Group")
        is ShapeLayer -> tr("studio.editor.editor_presentation.shape", "Shape")
    }
}

internal fun sceneLabel(kind: SceneKind): String = when (kind) {
    SceneKind.SINGLE -> tr("studio.editor.editor_presentation.single_device", "Single device")
    SceneKind.CAPTION_OVER_DEVICE -> tr("studio.editor.editor_presentation.caption_over_device", "Caption over device")
    SceneKind.TILTED_PAIR -> tr("studio.editor.editor_presentation.tilted_pair", "Tilted pair")
    SceneKind.FAN_OF_THREE -> tr("studio.editor.editor_presentation.fan_of_three", "Fan of three")
    SceneKind.FLOATING_CARDS -> tr("studio.editor.editor_presentation.floating_cards", "Floating cards")
    SceneKind.PANORAMA -> tr("studio.editor.editor_presentation.panorama", "Panorama")
}

internal fun sceneDescription(kind: SceneKind): String = when (kind) {
    SceneKind.SINGLE -> tr("studio.editor.editor_presentation.one_device_centred_on_the_artboard", "One device, centred on the artboard.")
    SceneKind.CAPTION_OVER_DEVICE -> tr("studio.editor.editor_presentation.a_caption_above_a_single_device", "A caption above a single device.")
    SceneKind.TILTED_PAIR -> tr("studio.editor.editor_presentation.two_devices_tilted_toward_each_other", "Two devices tilted toward each other.")
    SceneKind.FAN_OF_THREE -> tr("studio.editor.editor_presentation.three_devices_in_a_spread", "Three devices in a spread.")
    SceneKind.FLOATING_CARDS -> tr("studio.editor.editor_presentation.captures_arranged_as_floating_cards", "Captures arranged as floating cards.")
    SceneKind.PANORAMA -> tr("studio.editor.editor_presentation.one_continuous_scene_across_the_shots", "One continuous scene across the shots.")
}

/** Breakpoints use the workspace's measured width, including when embedded in the app shell. */
internal data class StudioPanelLayout(val layers: Boolean, val properties: Boolean)
internal fun studioPanelLayout(width: Int): StudioPanelLayout = StudioPanelLayout(width >= 1024, width >= 1280)
